import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createFormTemplate,
  createFormTemplateVersion,
  getFormTemplates,
  getFormTemplateVersions,
  publishFormTemplateVersion,
  updateFormTemplate,
} from "../../api/formTemplates";

const TAB_AVAILABLE = "available";
const TAB_OWN = "own";
const PERSONAL_FIELD_TYPES = [
  { value: "text", label: "Tekst" },
  { value: "textarea", label: "Lange tekst" },
  { value: "number", label: "Nummer" },
  { value: "date", label: "Datum" },
  { value: "checkbox", label: "Checkbox" },
  { value: "select", label: "Keuzelijst" },
  { value: "photo", label: "Foto" },
  { value: "signature", label: "Handtekening" },
  { value: "sectionTitle", label: "Sectietitel" },
];

function createDefaultEditorTemplate() {
  return {
    id: "",
    name: "",
    description: "",
    type: "checklist",
    fields: [
      {
        id: uid("fld"),
        type: "text",
        label: "Nieuw veld",
        required: false,
        helpText: "",
        sortOrder: 1,
        optionsText: "",
      },
    ],
  };
}

export default function MonteurTemplatesPage() {
  const navigate = useNavigate();

  const [tab, setTab] = useState(TAB_AVAILABLE);
  const [search, setSearch] = useState("");

  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(true);
  const [availableError, setAvailableError] = useState("");

  const [ownTemplates, setOwnTemplates] = useState([]);
  const [ownLoading, setOwnLoading] = useState(true);
  const [ownError, setOwnError] = useState("");
  const [ownAccessDenied, setOwnAccessDenied] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState("");
  const [editorSuccess, setEditorSuccess] = useState("");
  const [editorTemplate, setEditorTemplate] = useState(createDefaultEditorTemplate());

  useEffect(() => {
    loadAvailableTemplates();
    loadOwnTemplates();
  }, []);

  async function loadAvailableTemplates() {
    try {
      setAvailableLoading(true);
      setAvailableError("");
      const data = await getFormTemplates({ includeAvailable: true });
      setAvailableTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setAvailableError(err?.response?.data?.message || err?.message || "Templates laden mislukt");
    } finally {
      setAvailableLoading(false);
    }
  }

  async function loadOwnTemplates() {
    try {
      setOwnLoading(true);
      setOwnError("");
      setOwnAccessDenied(false);
      const data = await getFormTemplates({ scope: "personal", own: true });
      setOwnTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      if (Number(err?.response?.status) === 403) {
        setOwnAccessDenied(true);
      }
      setOwnError(err?.response?.data?.message || err?.message || "Mijn templates laden mislukt");
    } finally {
      setOwnLoading(false);
    }
  }

  const filteredAvailable = useMemo(() => {
    return applyTemplateSearch(availableTemplates, search);
  }, [availableTemplates, search]);

  const filteredOwn = useMemo(() => {
    return applyTemplateSearch(ownTemplates, search);
  }, [ownTemplates, search]);

  function openCreateEditor() {
    setEditorTemplate(createDefaultEditorTemplate());
    setEditorError("");
    setEditorSuccess("");
    setEditorOpen(true);
  }

  async function openEditEditor(template) {
    try {
      setEditorError("");
      setEditorSuccess("");
      const versions = await getFormTemplateVersions(template?._id);
      const latest = Array.isArray(versions) ? versions[0] : null;
      const loadedFields = extractFieldsFromDefinition(latest?.definition);

      setEditorTemplate({
        id: String(template?._id || ""),
        name: String(template?.name || ""),
        description: String(template?.description || ""),
        type: String(template?.type || "checklist"),
        fields: loadedFields.length > 0 ? loadedFields : createDefaultEditorTemplate().fields,
      });
      setEditorOpen(true);
    } catch (err) {
      setOwnError(err?.response?.data?.message || err?.message || "Template laden mislukt");
    }
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditorError("");
    setEditorSuccess("");
  }

  function setTemplateField(key, value) {
    setEditorTemplate((prev) => ({ ...prev, [key]: value }));
  }

  function addField() {
    setEditorTemplate((prev) => {
      const nextOrder = (prev.fields || []).length + 1;
      return {
        ...prev,
        fields: [
          ...(prev.fields || []),
          {
            id: uid("fld"),
            type: "text",
            label: `Veld ${nextOrder}`,
            required: false,
            helpText: "",
            sortOrder: nextOrder,
            optionsText: "",
          },
        ],
      };
    });
  }

  function removeField(fieldId) {
    setEditorTemplate((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((field) => field.id !== fieldId),
    }));
  }

  function updateField(fieldId, key, value) {
    setEditorTemplate((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((field) =>
        field.id === fieldId
          ? {
              ...field,
              [key]: value,
            }
          : field
      ),
    }));
  }

  async function saveTemplate(targetStatus) {
    const name = String(editorTemplate?.name || "").trim();
    if (!name) {
      setEditorError("Naam is verplicht");
      return;
    }

    if (!Array.isArray(editorTemplate.fields) || editorTemplate.fields.length === 0) {
      setEditorError("Voeg minimaal 1 veld toe");
      return;
    }

    const normalizedStatus = targetStatus === "active" ? "active" : "draft";

    try {
      setEditorSaving(true);
      setEditorError("");
      setEditorSuccess("");

      const definition = buildDefinitionFromEditor(editorTemplate.fields);

      if (!editorTemplate.id) {
        await createFormTemplate({
          name,
          description: String(editorTemplate?.description || "").trim(),
          type: String(editorTemplate?.type || "checklist"),
          category: "PERSONAL",
          scope: "personal",
          status: normalizedStatus,
          definition,
        });
      } else {
        await updateFormTemplate(editorTemplate.id, {
          name,
          description: String(editorTemplate?.description || "").trim(),
          type: String(editorTemplate?.type || "checklist"),
          status: normalizedStatus === "active" ? "active" : "draft",
        });

        const createdVersion = await createFormTemplateVersion(editorTemplate.id, {
          status: "draft",
          definition,
        });

        if (normalizedStatus === "active") {
          await publishFormTemplateVersion(createdVersion?._id);
        }
      }

      setEditorSuccess(
        normalizedStatus === "active"
          ? "Template opgeslagen en geactiveerd"
          : "Template opgeslagen als concept"
      );

      await Promise.all([loadOwnTemplates(), loadAvailableTemplates()]);
      setTab(TAB_OWN);
      setEditorOpen(false);
    } catch (err) {
      setEditorError(err?.response?.data?.message || err?.message || "Template opslaan mislukt");
    } finally {
      setEditorSaving(false);
    }
  }

  async function archiveTemplate(templateId) {
    try {
      await updateFormTemplate(templateId, { status: "archived" });
      await Promise.all([loadOwnTemplates(), loadAvailableTemplates()]);
    } catch (err) {
      setOwnError(err?.response?.data?.message || err?.message || "Template archiveren mislukt");
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Formulieren & Mijn templates</h2>
        <p className="mt-1 text-sm text-gray-600">
          Gebruik bedrijfstemplates of maak je eigen persoonlijke templates voor jouw werkbonnen, projecten en controles.
        </p>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded border border-gray-300 p-1">
            <button
              type="button"
              onClick={() => setTab(TAB_AVAILABLE)}
              className={`rounded px-3 py-1.5 text-sm ${
                tab === TAB_AVAILABLE ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Beschikbaar voor mij
            </button>
            <button
              type="button"
              onClick={() => setTab(TAB_OWN)}
              className={`rounded px-3 py-1.5 text-sm ${
                tab === TAB_OWN ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Mijn templates
            </button>
          </div>

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Zoek op naam, type of categorie"
            className="w-full max-w-sm rounded border p-2 text-sm"
          />
        </div>

        {tab === TAB_AVAILABLE && (
          <TemplateGrid
            templates={filteredAvailable}
            loading={availableLoading}
            error={availableError}
            emptyText="Geen templates gevonden."
            onStart={(templateId) => navigate(`/monteur/forms/${templateId}/run`)}
          />
        )}

        {tab === TAB_OWN && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500">Persoonlijke templates zijn alleen zichtbaar voor jou.</p>
              {!ownAccessDenied && (
                <button
                  type="button"
                  onClick={openCreateEditor}
                  className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Nieuwe template
                </button>
              )}
            </div>

            {ownAccessDenied ? (
              <p className="text-sm text-red-600">Je hebt geen toegang tot Mijn templates.</p>
            ) : (
              <OwnTemplateGrid
                templates={filteredOwn}
                loading={ownLoading}
                error={ownError}
                onStart={(templateId) => navigate(`/monteur/forms/${templateId}/run`)}
                onEdit={openEditEditor}
                onArchive={archiveTemplate}
              />
            )}
          </div>
        )}
      </section>

      {editorOpen && (
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900">
            {editorTemplate.id ? "Mijn template bewerken" : "Nieuwe persoonlijke template"}
          </h3>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Naam *</span>
              <input
                value={editorTemplate.name}
                onChange={(event) => setTemplateField("name", event.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                placeholder="Bijv. Controle noodverlichting"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Type</span>
              <select
                value={editorTemplate.type}
                onChange={(event) => setTemplateField("type", event.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="checklist">Checklist</option>
                <option value="inspection">Inspectie</option>
                <option value="site_survey">Site Survey</option>
                <option value="opname">Opname</option>
                <option value="toolbox">Toolbox</option>
              </select>
            </label>

            <label className="block md:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">Omschrijving</span>
              <textarea
                value={editorTemplate.description}
                onChange={(event) => setTemplateField("description", event.target.value)}
                className="min-h-[80px] w-full rounded border border-gray-300 px-3 py-2 text-sm"
                placeholder="Waar gebruik je deze template voor?"
              />
            </label>
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Velden</h4>
              <button
                type="button"
                onClick={addField}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50"
              >
                Veld toevoegen
              </button>
            </div>

            {editorTemplate.fields.map((field) => (
              <article key={field.id} className="rounded border border-gray-200 p-3">
                <div className="grid gap-2 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Type</span>
                    <select
                      value={field.type}
                      onChange={(event) => updateField(field.id, "type", event.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    >
                      {PERSONAL_FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Volgorde</span>
                    <input
                      type="number"
                      min="1"
                      value={field.sortOrder}
                      onChange={(event) => updateField(field.id, "sortOrder", Number(event.target.value || 1))}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Label</span>
                    <input
                      value={field.label}
                      onChange={(event) => updateField(field.id, "label", event.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Helptekst</span>
                    <input
                      value={field.helpText}
                      onChange={(event) => updateField(field.id, "helpText", event.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      placeholder="Optionele uitleg voor dit veld"
                    />
                  </label>

                  {field.type === "select" && (
                    <label className="block md:col-span-2">
                      <span className="mb-1 block text-xs font-medium text-gray-700">Opties (komma gescheiden)</span>
                      <input
                        value={field.optionsText}
                        onChange={(event) => updateField(field.id, "optionsText", event.target.value)}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        placeholder="Bijv. OK, Niet OK, N.v.t."
                      />
                    </label>
                  )}

                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(field.required)}
                      onChange={(event) => updateField(field.id, "required", event.target.checked)}
                    />
                    Verplicht veld
                  </label>
                </div>

                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => removeField(field.id)}
                    className="rounded border border-red-300 bg-white px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                  >
                    Verwijderen
                  </button>
                </div>
              </article>
            ))}
          </div>

          {editorError && <p className="mt-3 text-sm text-red-600">{editorError}</p>}
          {editorSuccess && <p className="mt-3 text-sm text-emerald-700">{editorSuccess}</p>}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={editorSaving}
              onClick={() => saveTemplate("draft")}
              className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-60"
            >
              {editorSaving ? "Opslaan..." : "Opslaan als concept"}
            </button>
            <button
              type="button"
              disabled={editorSaving}
              onClick={() => saveTemplate("active")}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {editorSaving ? "Opslaan..." : "Opslaan en activeren"}
            </button>
            <button
              type="button"
              disabled={editorSaving}
              onClick={closeEditor}
              className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-60"
            >
              Annuleren
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function TemplateGrid({ templates, loading, error, emptyText, onStart }) {
  if (loading) return <p className="text-sm text-gray-500">Laden...</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!templates.length) return <p className="text-sm text-gray-500">{emptyText}</p>;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {templates.map((item) => {
        const status = String(item?.status || "").toLowerCase();
        const canStart = status === "active";

        return (
          <article key={item._id} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">{item.type || "-"}</p>
            <h3 className="mt-1 text-base font-semibold text-gray-900">{item.name || "-"}</h3>
            <p className="mt-1 text-xs text-gray-600">Categorie: {item.category || "-"}</p>
            <p className="mt-1 text-xs text-gray-600">Status: {item.status || "-"}</p>
            <p className="mt-2 line-clamp-3 text-sm text-gray-700">{item.description || "Geen omschrijving"}</p>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={!canStart}
                onClick={() => onStart(item._id)}
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Formulier starten
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function OwnTemplateGrid({ templates, loading, error, onStart, onEdit, onArchive }) {
  if (loading) return <p className="text-sm text-gray-500">Laden...</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!templates.length) return <p className="text-sm text-gray-500">Nog geen persoonlijke templates.</p>;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {templates.map((item) => {
        const status = String(item?.status || "").toLowerCase();
        const canStart = status === "active";
        const archived = status === "archived";

        return (
          <article key={item._id} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Persoonlijk · {item.type || "-"}</p>
            <h3 className="mt-1 text-base font-semibold text-gray-900">{item.name || "-"}</h3>
            <p className="mt-1 text-xs text-gray-600">Status: {item.status || "-"}</p>
            <p className="mt-2 line-clamp-3 text-sm text-gray-700">{item.description || "Geen omschrijving"}</p>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!canStart}
                onClick={() => onStart(item._id)}
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Starten
              </button>
              <button
                type="button"
                disabled={archived}
                onClick={() => onEdit(item)}
                className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Bewerken
              </button>
              <button
                type="button"
                disabled={archived}
                onClick={() => onArchive(item._id)}
                className="rounded border border-red-300 bg-white px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Archiveren
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function extractFieldsFromDefinition(definition) {
  const firstSection = Array.isArray(definition?.sections) ? definition.sections[0] : null;
  const rawFields = Array.isArray(firstSection?.fields) ? firstSection.fields : [];
  return rawFields.map((field, index) => ({
    id: String(field?.id || uid("fld")),
    type: String(field?.type || "text"),
    label: String(field?.label || "Veld"),
    required: Boolean(field?.required),
    helpText: String(field?.helpText || ""),
    sortOrder: Number.isFinite(Number(field?.sortOrder)) ? Number(field?.sortOrder) : index + 1,
    optionsText: Array.isArray(field?.options) ? field.options.join(", ") : "",
  }));
}

function buildDefinitionFromEditor(fields = []) {
  const normalizedFields = [...fields]
    .map((field, index) => {
      const type = String(field?.type || "text");
      const options = type === "select"
        ? String(field?.optionsText || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : [];

      return {
        id: String(field?.id || uid("fld")),
        type,
        label: String(field?.label || `Veld ${index + 1}`).trim() || `Veld ${index + 1}`,
        required: Boolean(field?.required),
        helpText: String(field?.helpText || "").trim(),
        sortOrder: Number.isFinite(Number(field?.sortOrder)) ? Number(field?.sortOrder) : index + 1,
        options,
      };
    })
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));

  return {
    sections: [
      {
        id: "section_personal_1",
        title: "Mijn checklist",
        repeatable: false,
        fields: normalizedFields,
      },
    ],
  };
}

function applyTemplateSearch(templates = [], search = "") {
  const needle = String(search || "").trim().toLowerCase();
  if (!needle) return templates;

  return templates.filter((item) => {
    const haystack = `${item?.name || ""} ${item?.type || ""} ${item?.category || ""} ${item?.status || ""}`.toLowerCase();
    return haystack.includes(needle);
  });
}

function uid(prefix = "id") {
  const random = Math.random().toString(36).slice(2, 9);
  return `${prefix}_${Date.now().toString(36)}_${random}`;
}
